/**
 * POS Settings API
 * 
 * Handles reading and updating POS configuration settings via SysConfig API
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { v2Unscoped, v2api } from './v2'

// ============================================================================
// Types
// ============================================================================

export interface POSSettings {
    // Printer Settings
    'pos.printer.width'?: number
    'pos.printer.type'?: string
    'frontend.print_receipt_clerk'?: boolean
    'pos.printer.print_congratulations_line'?: boolean
    // Post-tender flow on the cashier UI. Mirrors BE PosReceiptCompleteAction:
    //   'ask'     → no auto-print; show Print Receipt + Done (default)
    //   'print'   → auto-print; show Done
    //   'nothing' → no auto-print; show Done
    'pos.receipt.complete_action'?: 'ask' | 'print' | 'nothing'

    // Cash Management
    'frontend.starting_cash'?: number
    // `pos.cashout_by_clerk` deliberately omitted -- the BE flag exists
    // but is never exposed for FE-side toggling. See the comment block
    // in SettingsModal.tsx where the UI control used to live. The flag
    // produces phantom variances at every waio store's actual workflow
    // (shared drawer, only the closer cashes out); set via SQL or admin
    // only.
    'frontend.always_open_drawer'?: boolean

    // Authentication
    'pos.jwt_expiry_hours'?: number
    'pos.jwt_refresh_days'?: number
    // Seconds of inactivity before the POS routes back to the PIN view
    'pos.logout_timeout'?: number

    // Customer Settings
    'frontend.customer_required'?: boolean
    'frontend.default_customer_id'?: number

    // Discount Settings
    'pos.discounts_disabled'?: boolean
    'pos.apply_rewards_discounts_on_final_total'?: boolean

    // Display Settings
    'frontend.hide_out_of_stock'?: boolean

    // Other Settings
    [key: string]: string | number | boolean | undefined
}

export interface UpdateSettingsRequest {
    set: Partial<POSSettings>
    store_id: number | null
}

export interface UpdateSettingsResponse {
    status: 'ok' | 'error'
}

// ============================================================================
// Query Keys
// ============================================================================

export const settingsKeys = {
    all: ['settings'] as const,
    store: (storeId: number | null) => [...settingsKeys.all, 'store', storeId] as const,
    pattern: (storeId: number | null, pattern: string) =>
        [...settingsKeys.store(storeId), 'pattern', pattern] as const,
    keys: (storeId: number | null, keys: string[]) =>
        [...settingsKeys.store(storeId), 'keys', keys] as const,
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Get POS settings by regex pattern
 */
export function usePOSSettings(
    storeId: number | null,
    pattern: string = 'pos.*,frontend.*'
) {
    return useQuery({
        queryKey: settingsKeys.pattern(storeId, pattern),
        queryFn: async (): Promise<POSSettings> => {
            try {
                const params: Record<string, string | number> = {
                    re_keys: pattern,
                }

                if (storeId !== null) {
                    params.store_id = storeId
                }

                const data = await v2api.get<POSSettings>(v2Unscoped('/sys_config'), params)

                // Return empty object if data is undefined
                return data || {}
            } catch (error) {
                console.error('Failed to fetch POS settings:', error)
                // Return empty object on error
                return {}
            }
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
    })
}

/**
 * Get specific POS settings by keys
 */
export function usePOSSettingsByKeys(
    storeId: number | null,
    keys: string[]
) {
    return useQuery({
        queryKey: settingsKeys.keys(storeId, keys),
        queryFn: async (): Promise<POSSettings> => {
            try {
                const params: Record<string, string | number> = {
                    keys: keys.join(','),
                }

                if (storeId !== null) {
                    params.store_id = storeId
                }

                const data = await v2api.get<POSSettings>(v2Unscoped('/sys_config'), params)

                return data || {}
            } catch (error) {
                console.error('Failed to fetch POS settings by keys:', error)
                return {}
            }
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        enabled: keys.length > 0,
    })
}

/**
 * Get a single POS setting
 */
export function usePOSSetting<T = string | number | boolean>(
    storeId: number | null,
    key: string,
    defaultValue?: T
) {
    return useQuery({
        queryKey: [...settingsKeys.store(storeId), 'key', key],
        queryFn: async (): Promise<T | undefined> => {
            try {
                const params: Record<string, string | number> = {
                    key,
                }

                if (defaultValue !== undefined) {
                    params.default = String(defaultValue)
                }

                if (storeId !== null) {
                    params.store_id = storeId
                }

                const data = await v2api.get<Record<string, T>>(v2Unscoped('/sys_config'), params)

                return data?.[key] ?? defaultValue
            } catch (error) {
                console.error('Failed to fetch POS setting:', error)
                return defaultValue
            }
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
    })
}

/**
 * Update POS settings
 */
export function useUpdatePOSSettings() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
            settings,
            storeId,
        }: {
            settings: Partial<POSSettings>
            storeId: number | null
        }): Promise<UpdateSettingsResponse> => {
            const payload: UpdateSettingsRequest = {
                set: settings,
                store_id: storeId,
            }

            return await v2api.post<UpdateSettingsResponse>(
                v2Unscoped('/sys_config'),
                payload
            )
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: settingsKeys.store(variables.storeId),
            })
        },
    })
}

/**
 * Update a single POS setting
 */
export function useUpdatePOSSetting() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
            key,
            value,
            storeId,
        }: {
            key: string
            value: string | number | boolean
            storeId: number | null
        }): Promise<UpdateSettingsResponse> => {
            return await v2api.post<UpdateSettingsResponse>(
                v2Unscoped('/sys_config'),
                {
                    key,
                    value,
                    store_id: storeId,
                }
            )
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: settingsKeys.store(variables.storeId),
            })
        },
    })
}

// ============================================================================
// Default Settings
// ============================================================================

export const DEFAULT_POS_SETTINGS: Partial<POSSettings> = {
    'pos.printer.width': 42,
    'pos.jwt_expiry_hours': 12,
    'pos.jwt_refresh_days': 7,
    'frontend.starting_cash': 0,
    'frontend.customer_required': false,
    'pos.discounts_disabled': false,
    'frontend.always_open_drawer': false,
    'frontend.hide_out_of_stock': false,
    'pos.printer.print_congratulations_line': false,
    'frontend.print_receipt_clerk': true,
    'pos.apply_rewards_discounts_on_final_total': false,
    'pos.receipt.complete_action': 'ask',
}
